import { AxiosInstance } from "axios";

import type { PlaceholderMaterializeTarget } from "../adapters/proposalToGrid";
import type { Proposal, ProposalPlanGroup } from "../types/proposal.types";

const PROPOSAL_PLAN_GROUPS_BASE_URL = "/btx/v1/proposal-plan-groups";
const PROPOSAL_PLAN_BASE_URL = "/btx/v1/proposal-plans";

/**
 * Deletes a proposal plan group
 *
 * Uses BTX endpoint: DELETE /api/btx/v1/proposal-plan-groups/:id
 *
 * @param id - Proposal plan group ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteProposalPlanGroup = async (
  id: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(`${PROPOSAL_PLAN_GROUPS_BASE_URL}/${id}`);
};

/**
 * Brings a deleted quote back out of Quote History (CD-89).
 *
 * Returns the restored group, whose `isActiveVersion` says where it landed: back on the
 * board, or in Quote History if a Replace superseded it while it sat deleted. Restoring
 * never promotes, so both outcomes are possible from the same click.
 *
 * Uses BTX endpoint: POST /api/btx/v1/proposal-plan-groups/:id/restore
 */
export const restoreProposalPlanGroup = async (
  id: string,
  axiosInstance: AxiosInstance,
): Promise<ProposalPlanGroup> => {
  const response = await axiosInstance.post<ProposalPlanGroup>(
    `${PROPOSAL_PLAN_GROUPS_BASE_URL}/${id}/restore`,
  );

  return response.data;
};

/**
 * Dismisses an "awaiting quote" placeholder column. The placeholder has no
 * persisted row, so the backend materializes it into a real group and
 * immediately soft-deletes it (dismiss:true)
 *
 * Uses BTX endpoint: POST /api/btx/v1/rfps/:rfpId/proposals
 */
export const dismissPlaceholderProposal = async (
  rfpId: string,
  target: PlaceholderMaterializeTarget,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  const hasResolvedName =
    !!target.carrierName && target.carrierName !== target.carrierId;

  await axiosInstance.post(`/btx/v1/rfps/${rfpId}/proposals`, {
    carrierId: target.carrierId,
    ...(hasResolvedName ? { carrierName: target.carrierName } : {}),
    origin: "analyst",
    materialize: {
      rfpBenefitPlanGroupId: target.rfpBenefitPlanGroupId,
      planPosition: target.planPosition,
      dismiss: true,
    },
  });
};

/**
 * Toggles an under-quoted group's empty pad slots (CD-1612 partial fill):
 * padSuppressed=true stops read-time padding to the linked RFP group's size
 * from synthesizing the empty columns for this group; padSuppressed=false
 * undoes that.
 *
 * Uses BTX endpoint: PATCH /api/btx/v1/proposal-plan-groups/:id
 */
export const suppressProposalPlanGroupPads = async (
  id: string,
  axiosInstance: AxiosInstance,
  padSuppressed = true,
): Promise<void> => {
  await axiosInstance.patch(`${PROPOSAL_PLAN_GROUPS_BASE_URL}/${id}`, {
    padSuppressed,
  });
};

/**
 * Duplicates a proposal plan group
 *
 * @param id - Proposal plan group ID to duplicate
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when duplication is complete
 */
export const duplicateProposalPlanGroup = async (
  id: string,
  axiosInstance: AxiosInstance,
): Promise<ProposalPlanGroup> => {
  const response = await axiosInstance.post<ProposalPlanGroup>(
    `${PROPOSAL_PLAN_GROUPS_BASE_URL}/${id}/duplicate`,
  );

  return response.data;
};

/**
 * Duplicates the entire proposal coverage owning plan group `id` — every plan group under it,
 * not just `id`'s — into a single brand new Proposal (carrier column) on the same carrier.
 * Unlike duplicateProposalPlanGroup, which adds a sibling group to the existing carrier column,
 * this creates one new, independent column mirroring every one of the original's alternates.
 *
 * @param id - Any proposal plan group ID under the coverage to duplicate
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to the newly created proposal
 */
export const duplicateProposalCoverage = async (
  id: string,
  axiosInstance: AxiosInstance,
): Promise<Proposal> => {
  const response = await axiosInstance.post<Proposal>(
    `${PROPOSAL_PLAN_GROUPS_BASE_URL}/${id}/duplicate-coverage`,
  );

  return response.data;
};

/**
 * Moves a carrier's proposal plan group to a different requested benefit
 * plan group's SBS page. The target must have the same coverage and
 * product type as the group being moved.
 *
 * Uses BTX endpoint: PATCH /api/btx/v1/proposal-plan-groups/:id/move
 */
export const moveProposalPlanGroup = async (
  groupId: string,
  rfpBenefitPlanGroupId: string,
  axiosInstance: AxiosInstance,
): Promise<ProposalPlanGroup> => {
  const response = await axiosInstance.patch<ProposalPlanGroup>(
    `${PROPOSAL_PLAN_GROUPS_BASE_URL}/${encodeURIComponent(groupId)}/move`,
    { rfpBenefitPlanGroupId },
  );

  return response.data;
};

/**
 * Deletes a proposal plan
 *
 * Uses BTX endpoint: DELETE /api/btx/v1/proposal-plans/:id
 *
 * @param id - Proposal plan ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteProposalPlan = async (
  id: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(`${PROPOSAL_PLAN_BASE_URL}/${id}`);
};

/**
 * Restores a deleted proposal plan
 *
 * Uses BTX endpoint: POST /api/btx/v1/proposal-plans/:id/restore
 *
 * @param id - Proposal plan group ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when restoring is complete
 */
export const restoreProposalPlan = async (
  id: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.post(`${PROPOSAL_PLAN_BASE_URL}/${id}/restore`);
};
